// Trig / function-graph curve generators + trig keypoint computation
// (pure math on plain params — no DOM, no D3, no component state).
//
// MOVED here verbatim from InteractiveGraph.js (module-level pure functions;
// the only change is the `export ` keyword on each moved top-level declaration)
// as maintenance-only Slice 2 of the InteractiveGraph split — same pattern as
// Slice 1 (#42, utils/{chartLayout,legendMetrics,functionGraphLabels}.js).
// InteractiveGraph imports computeTrigKeypoints / generateSyntheticCurve /
// generateMathCurve back, so every renderer call site is unchanged. Behavior
// must stay byte-identical: do NOT add component-/state-aware logic here.
//
// NOTE: TRIG_STAGE_MODE_ORDER / resolveTrigDisplayMode / resolveFuncDisplayMode
// intentionally stay in InteractiveGraph.js (stage-display policy, not in the
// Slice 2 candidate set).

export const computeTrigKeypoints = (params = {}) => {
  const kind = (params.kind || params.type || 'sin').toLowerCase();
  const amp  = parseFloat(params.amplitude) || parseFloat(params.a) || 1;
  const freq = parseFloat(params.frequency) || parseFloat(params.k) || 1;
  const phase = parseFloat(params.phase) || parseFloat(params.phi) || 0;
  const shift = parseFloat(params.shift) || parseFloat(params.c) || 0;
  const period = (2 * Math.PI) / Math.abs(freq);
  const startX = -phase / freq;

  const evalFn = (x) => {
    const angle = freq * x + phase;
    if (kind.startsWith('sin')) return amp * Math.sin(angle) + shift;
    if (kind.startsWith('cos')) return amp * Math.cos(angle) + shift;
    const v = amp * Math.tan(angle) + shift;
    return Number.isFinite(v) && Math.abs(v) < 100 ? v : null;
  };

  let raw;
  if (kind.startsWith('sin')) {
    raw = [
      { x: startX,                  name: '始点', nameKey: 'start' },
      { x: startX + period / 4,     name: '最高点', nameKey: 'maxPoint' },
      { x: startX + period / 2,     name: 'x軸交点', nameKey: 'xIntercept' },
      { x: startX + 3 * period / 4, name: '最低点', nameKey: 'minPoint' },
      { x: startX + period,         name: '終点', nameKey: 'end' },
    ];
  } else if (kind.startsWith('cos')) {
    raw = [
      { x: startX,                  name: '最高点', nameKey: 'maxPoint' },
      { x: startX + period / 4,     name: 'x軸交点', nameKey: 'xIntercept' },
      { x: startX + period / 2,     name: '最低点', nameKey: 'minPoint' },
      { x: startX + 3 * period / 4, name: 'x軸交点', nameKey: 'xIntercept' },
      { x: startX + period,         name: '終点', nameKey: 'end' },
    ];
  } else {
    const tp = Math.PI / Math.abs(freq);
    raw = [
      { x: startX - tp / 4,       name: 'tan = -1' },
      { x: startX,                 name: '原点', nameKey: 'origin' },
      { x: startX + tp / 4,       name: 'tan = 1' },
      { x: startX + tp,           name: 'x軸交点', nameKey: 'xIntercept' },
      { x: startX + tp + tp / 4,  name: 'tan = 1' },
    ];
  }
  return raw
    .map(kp => ({ ...kp, y: evalFn(kp.x) }))
    .filter(kp => kp.y !== null)
    .map(kp => ({ ...kp, y: Math.round(kp.y * 1000) / 1000 }));
};

export const generateSyntheticCurve = (graphType) => {
  const n = 100;
  const isTrig = graphType === 'trigonometric_function';
  const lo = isTrig ? -2 * Math.PI : -10;
  const hi = isTrig ? 2 * Math.PI : 10;
  const step = (hi - lo) / (n - 1);
  const pts = [];
  for (let i = 0; i < n; i++) {
    const x = lo + step * i;
    let y = null;
    switch (graphType) {
      case 'trigonometric_function':   y = Math.sin(x); break;
      case 'linear_function':
      case 'proportion':               y = x; break;
      case 'quadratic_function':       y = x * x; break;
      case 'exponential_function':     y = Math.exp(x); break;
      case 'logarithmic_function':     if (x > 0) y = Math.log(x); break;
      case 'inverse_proportion':       if (Math.abs(x) > 0.1) y = 1 / x; break;
      default: break;
    }
    if (y !== null && Number.isFinite(y) && Math.abs(y) < 100) pts.push({ x, y });
  }
  return pts;
};

export const generateMathCurve = (params = {}, pointCount = 200) => {
  const kind = (params.kind || params.type || 'sin').toLowerCase();
  const amp = parseFloat(params.amplitude) || parseFloat(params.a) || 1;
  const freq = parseFloat(params.frequency) || parseFloat(params.k) || 1;
  const phase = parseFloat(params.phase) || parseFloat(params.phi) || 0;
  const shift = parseFloat(params.shift) || parseFloat(params.c) || 0;
  const period = (2 * Math.PI) / Math.abs(freq);
  const center = -phase / freq;
  const lo = center - 1.5 * period;
  const hi = center + 1.5 * period;
  const step = (hi - lo) / (pointCount - 1);
  const yLimit = Math.abs(amp) * 10;
  const pts = [];
  for (let i = 0; i < pointCount; i++) {
    const x = lo + step * i;
    const angle = freq * x + phase;
    let y;
    if (kind.startsWith('cos')) {
      y = amp * Math.cos(angle) + shift;
    } else if (kind.startsWith('tan')) {
      y = amp * Math.tan(angle) + shift;
      if (!Number.isFinite(y) || Math.abs(y) > yLimit) continue;
    } else {
      y = amp * Math.sin(angle) + shift;
    }
    if (Number.isFinite(y)) {
      pts.push({ x: Math.round(x * 10000) / 10000, y: Math.round(y * 10000) / 10000 });
    }
  }
  return pts;
};
